import { useState } from "react";
import { ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useProducts } from "@/context/ProductsContext";
import { LEMONADE_ADDONS } from "@/data/products";
import { toast } from "sonner";

const ICE_LEVELS = ["No Ice", "Light", "Regular"];

const LemonadeCard = ({ product }) => {
  const { add } = useCart();
  const { products } = useProducts();
  const [ice, setIce] = useState("Regular");
  const [sweetener, setSweetener] = useState("regular");
  const [picks, setPicks] = useState([]);
  const [showAddons, setShowAddons] = useState(false);
  const [addons, setAddons] = useState([]);
  const isCombo = product.cat === "Combos";
  const isFall = product.cat === "Fall Flavors";
  const needsPicks = product.pick > 0;
  const picksReady = !needsPicks || (picks.length === product.pick && picks.every(Boolean));
  const soldOut = product.in_stock === false;

  const flavorNames = products
    .filter((p) => p.brand === "lemonade" && ["Classic", "Flavored"].includes(p.cat) && p.in_stock !== false)
    .map((p) => p.name.replace(" — 34oz", ""));

  const chosenAddons = LEMONADE_ADDONS.filter((a) => addons.includes(a.id));
  const finalPrice = product.price + chosenAddons.reduce((s, a) => s + a.price, 0);

  const toggleAddon = (id) =>
    setAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));

  const setPick = (i, val) => {
    setPicks((prev) => {
      const next = [...prev];
      next[i] = val;
      return next;
    });
  };

  const handleAdd = () => {
    const parts = needsPicks
      ? picks.map((p, i) => `#${i + 1} ${p}`)
      : [`Ice: ${ice}`];
    parts.push(sweetener === "keto" ? "Keto / Diabetic sweetener" : "Regular sugar");
    chosenAddons.forEach((a) => parts.push(`+${a.name}`));
    add({ ...product, price: finalPrice }, parts.join(" · "), addons);
    toast.success(`${product.name} added to your bag`);
  };

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="bg-white rounded-2xl border-2 border-[#1a1a1a] shadow-[6px_6px_0_#ec4899] overflow-hidden flex flex-col"
    >
      <div className="relative">
        {isCombo && (
          <div className="absolute top-0 inset-x-0 z-10 bg-yellow-300 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-[#1a1a1a] py-1">
            🎉 Combo Deal
          </div>
        )}
        {isFall && (
          <div className="absolute top-0 inset-x-0 z-10 bg-orange-500 text-center text-[10px] font-bold uppercase tracking-[0.2em] text-white py-1">
            🍂 Fall Drop
          </div>
        )}
        <img src={product.image} alt={product.name} loading="lazy" className={`w-full h-44 object-cover ${soldOut ? "grayscale opacity-60" : ""}`} />
        {soldOut && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-[#1a1a1a]/90 px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white" data-testid={`sold-out-${product.id}`}>
              Sold Out
            </span>
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col gap-2 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-pink-500">{product.cat}</p>
        <h3 className="font-bold text-[#1a1a1a] leading-snug">{product.name}</h3>
        <p className="text-sm text-zinc-500 leading-relaxed">{product.description}</p>
        {needsPicks ? (
          <div className="mt-1 space-y-2" data-testid={`pick-flavors-${product.id}`}>
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-600">👉 Pick {product.pick} flavors</p>
            {Array.from({ length: product.pick }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="text-xs font-bold text-zinc-500 w-5">#{i + 1}</span>
                <select
                  data-testid={`pick-${i}-${product.id}`}
                  value={picks[i] || ""}
                  onChange={(e) => setPick(i, e.target.value)}
                  className="flex-1 rounded-lg border-2 border-zinc-200 bg-white px-2 py-1.5 text-sm text-[#1a1a1a] focus:border-pink-400 focus:outline-none"
                >
                  <option value="">— Select —</option>
                  {flavorNames.map((f) => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-1.5">🧊 Ice level</p>
            <div className="flex gap-1.5">
              {ICE_LEVELS.map((level) => (
                <button
                  key={level}
                  data-testid={`ice-${level.toLowerCase().replace(" ", "-")}-${product.id}`}
                  onClick={() => setIce(level)}
                  disabled={soldOut}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                    ice === level
                      ? "bg-pink-500 border-pink-500 text-white"
                      : "border-zinc-300 text-zinc-500 hover:border-pink-400"
                  } disabled:opacity-40`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
        )}
        <div className="mt-1" data-testid={`sweetener-${product.id}`}>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-1.5">🍯 Sweetener</p>
          <div className="space-y-1">
            <label data-testid={`sweetener-regular-${product.id}`} className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
              <input type="checkbox" checked={sweetener === "regular"} onChange={() => setSweetener("regular")} disabled={soldOut} className="w-3.5 h-3.5 accent-pink-500" />
              Regular sugar
            </label>
            <label data-testid={`sweetener-keto-${product.id}`} className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
              <input type="checkbox" checked={sweetener === "keto"} onChange={() => setSweetener("keto")} disabled={soldOut} className="w-3.5 h-3.5 accent-pink-500" />
              Diabetic / Keto friendly
            </label>
          </div>
        </div>
        <div className="mt-1">
          <button
            data-testid={`toggle-addons-${product.id}`}
            onClick={() => setShowAddons(!showAddons)}
            disabled={soldOut}
            className="text-xs font-semibold text-pink-500 hover:text-pink-600 disabled:opacity-40"
          >
            {showAddons ? "− Hide add-ons" : "+ Add-ons"}
          </button>
          {showAddons && (
            <div className="mt-2 space-y-1.5" data-testid={`addons-list-${product.id}`}>
              {LEMONADE_ADDONS.map((a) => (
                <label key={a.id} data-testid={`addon-${a.id}-${product.id}`} className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={addons.includes(a.id)}
                    onChange={() => toggleAddon(a.id)}
                    className="w-4 h-4 rounded border-zinc-300 accent-pink-500"
                  />
                  <span className="flex-1">{a.emoji} {a.name}</span>
                  <span className="text-zinc-400 text-xs">+${a.price.toFixed(2)}</span>
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="mt-auto flex items-center justify-between pt-3">
          <span className="text-lg font-extrabold text-[#1a1a1a]" data-testid={`product-price-${product.id}`}>
            ${finalPrice.toFixed(2)}
          </span>
          <button
            data-testid={`add-to-cart-${product.id}`}
            onClick={handleAdd}
            disabled={!picksReady || soldOut}
            className="inline-flex items-center gap-1.5 rounded-full bg-pink-500 text-white text-sm font-semibold px-4 py-1.5 transition-transform hover:scale-105 active:scale-95 disabled:opacity-40 disabled:hover:scale-100"
          >
            <ShoppingBag className="w-3.5 h-3.5" /> {soldOut ? "Sold Out" : "Add"}
          </button>
        </div>
      </div>
    </article>
  );
};

const DarkCard = ({ product }) => {
  const { add } = useCart();
  const available = product.price != null;
  const soldOut = product.in_stock === false;

  return (
    <article
      data-testid={`product-card-${product.id}`}
      className="bg-[#111] rounded-xl border border-white/10 overflow-hidden flex flex-col"
    >
      <div className="relative">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className={`w-full h-44 object-cover ${available && !soldOut ? "" : "grayscale blur-[2px] opacity-70"}`}
        />
        {(!available || soldOut) && (
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-black/80 border border-white/25 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-white">
              {soldOut ? "Sold Out" : "Coming Soon"}
            </span>
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col gap-1.5 flex-1">
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-400/80">{product.cat}</p>
        <h3 className="font-bold text-white leading-snug">{product.name}</h3>
        <p className="text-sm text-zinc-400 leading-relaxed">{product.description}</p>
        <div className="mt-auto pt-3">
          {available ? (
            <div className="space-y-3">
              {product.size && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-400/80 mb-1">Size</p>
                  <div
                    data-testid={`size-select-${product.id}`}
                    className="rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-zinc-200"
                  >
                    {product.size} · ${product.price.toFixed(2)}
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-lg font-bold text-white" data-testid={`product-price-${product.id}`}>
                  ${product.price.toFixed(2)}
                </span>
                <button
                  data-testid={`add-to-cart-${product.id}`}
                  onClick={() => {
                    add(product, product.size ? `Size: ${product.size}` : "");
                    toast.success(`${product.name} added to your bag`);
                  }}
                  disabled={soldOut}
                  className="inline-flex items-center gap-1.5 rounded bg-amber-400 text-black text-xs font-bold uppercase tracking-wider px-4 py-2 transition-transform hover:scale-105 active:scale-95 disabled:opacity-40"
                >
                  <ShoppingBag className="w-3.5 h-3.5" /> {soldOut ? "Sold Out" : "Add"}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-sm italic text-zinc-500" data-testid={`product-tbd-${product.id}`}>
              Price TBD · Not yet available
            </p>
          )}
        </div>
      </div>
    </article>
  );
};

export const ProductCard = ({ product }) =>
  product.brand === "lemonade" ? <LemonadeCard product={product} /> : <DarkCard product={product} />;

export default ProductCard;
